import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { socketEventAggregator } from '../services/socketEventAggregator';
import {
  AuthTokenExpiredPayload,
  AuthInvalidTokenPayload,
  AuthProfileAPIFailedPayload,
  AuthSessionInvalidatedPayload,
  AuthUnauthorizedPayload,
  AuthTokenExpiringPayload,
} from '../services/socketEventAggregator';

export interface UseSocketAuthListenerOptions {
  onTokenExpired?: (payload: AuthTokenExpiredPayload) => void;
  onInvalidToken?: (payload: AuthInvalidTokenPayload) => void;
  onProfileAPIFailed?: (payload: AuthProfileAPIFailedPayload) => void;
  onSessionInvalidated?: (payload: AuthSessionInvalidatedPayload) => void;
  onUnauthorized?: (payload: AuthUnauthorizedPayload) => void;
  onTokenExpiringsoon?: (payload: AuthTokenExpiringPayload) => void;
  autoLogout?: boolean;
  showNotification?: (message: string, type: 'error' | 'warning' | 'info') => void;
}

/**
 * Hook to listen to authentication events and handle logout/redirect
 * 
 * Usage:
 * useSocketAuthListener({
 *   autoLogout: true,
 *   showNotification: (msg, type) => toast.show(msg, type)
 * });
 */
export const useSocketAuthListener = (options: UseSocketAuthListenerOptions = {}) => {
  const { logout } = useAuth0();
  const {
    onTokenExpired,
    onInvalidToken,
    onProfileAPIFailed,
    onSessionInvalidated,
    onUnauthorized,
    onTokenExpiringsoon,
    autoLogout = true,
    showNotification,
  } = options;

  useEffect(() => {
    // Handle token expired
    const handleTokenExpired = async (payload: AuthTokenExpiredPayload) => {
      console.warn('[SOCKET_PATH] Socket event received: auth:token_expired', payload);

      if (showNotification) {
        showNotification(
          'Your session has expired. Please login again.',
          'warning'
        );
      }

      onTokenExpired?.(payload);

      if (autoLogout) {
        // Give notification time to show
        setTimeout(() => {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }, 1000);
      }
    };

    // Handle invalid token
    const handleInvalidToken = async (payload: AuthInvalidTokenPayload) => {
      console.error('[SOCKET_PATH] Socket event received: auth:invalid_token', payload);

      if (showNotification) {
        showNotification('Invalid authentication token. Please login again.', 'error');
      }

      onInvalidToken?.(payload);

      if (autoLogout) {
        setTimeout(() => {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }, 1000);
      }
    };

    // Handle profile API failure
    const handleProfileAPIFailed = async (payload: AuthProfileAPIFailedPayload) => {
      // Suppress console error logging for profile API failures
      // These are expected when Web PubSub service is not configured
      console.debug('[SOCKET_PATH] Socket event received: auth:profile_api_failed (suppressed error logging)');

      if (showNotification) {
        showNotification(
          'Failed to load user profile. Please try logging in again.',
          'error'
        );
      }

      onProfileAPIFailed?.(payload);

      if (autoLogout && payload.action === 'logout_recommended') {
        setTimeout(() => {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }, 1500);
      }
    };

    // Handle session invalidated
    const handleSessionInvalidated = async (payload: AuthSessionInvalidatedPayload) => {
      console.warn('[SOCKET_PATH] Socket event received: auth:session_invalidated', payload);

      if (showNotification) {
        showNotification(
          `Session ended: ${payload.reason}. Redirecting to login...`,
          'warning'
        );
      }

      onSessionInvalidated?.(payload);

      if (payload.action === 'redirect_to_login') {
        setTimeout(() => {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }, 1500);
      }
    };

    // Handle unauthorized
    const handleUnauthorized = async (payload: AuthUnauthorizedPayload) => {
      console.warn('[SOCKET_PATH] Socket event received: auth:unauthorized', payload);

      if (showNotification) {
        showNotification(
          `You are not authorized to: ${payload.action}`,
          'warning'
        );
      }

      onUnauthorized?.(payload);
    };

    // Handle token expiring soon
    const handleTokenExpiringSoon = async (payload: AuthTokenExpiringPayload) => {

      if (showNotification) {
        showNotification(
          `Your session will expire in ${payload.minutes_until_expiry} minutes. Please refresh.`,
          'info'
        );
      }

      onTokenExpiringsoon?.(payload);
    };

    // Register all listeners
    const unsubscribers = [
      socketEventAggregator.on('auth:token_expired', handleTokenExpired),
      socketEventAggregator.on('auth:invalid_token', handleInvalidToken),
      socketEventAggregator.on('auth:profile_api_failed', handleProfileAPIFailed),
      socketEventAggregator.on('auth:session_invalidated', handleSessionInvalidated),
      socketEventAggregator.on('auth:unauthorized', handleUnauthorized),
      socketEventAggregator.on('auth:token_expiring_soon', handleTokenExpiringSoon),
    ];

    // Cleanup
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [logout, autoLogout, showNotification, onTokenExpired, onInvalidToken, onProfileAPIFailed, onSessionInvalidated, onUnauthorized, onTokenExpiringsoon]);

  return {
    eventAggregator: socketEventAggregator,
  };
};

export default useSocketAuthListener;
